import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, CardActionArea, Stack, Typography} from 'views/components';
import {MailOutline} from 'views/icons';
import {Commands, Translate, translate} from 'views/module';

import CardBody from './CardBody';

import backend from 'utils/backend';
import CLIENT_SOURCES from 'views/scenes/client/client_source';
import moment from 'moment';

const Card = (props) => {
  const {
    item,
    onItemUpdate,
    hasUnreadMessages,
    statusProgressWrap: progressWrap,
  } = props;
  const clientSource = props.clientSource || CLIENT_SOURCES.LEGACY;

  const [cardItem, setCardItem] = React.useState(item);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = React.useState(false);
  const [isCallStatusDropdownOpen, setIsCallStatusDropdownOpen] = React.useState(false);
  const [isCommandActive, setIsCommandActive] = React.useState(false);
  const [isCommandsFocused, setIsCommandsFocused] = React.useState(false);

  React.useEffect(() => {
    setCardItem(item);
  }, [item]);

  const clientCareplan = cardItem.clientCarePlan;
  const clientStatusLog = cardItem.clientLastLogStatusUpdate;
  const clientLastMessage = cardItem.clientLastMessage;
  const clientRn = cardItem.clientRn;
  const clientName = cardItem.clientName;
  const clientCreatedAt = cardItem.createdAt;
  const status = cardItem.status;
  const hasUnreadMessage = hasUnreadMessages(clientRn, clientLastMessage);
  const callStatus = cardItem.clientCallStatus?.introCallStatus;
  const leadQuality = clientCareplan?.leadQuality;
  const isCommandsActive = isCommandsFocused || isCommandActive;
  const isAnyOverlayOpen = isStatusDropdownOpen || isCallStatusDropdownOpen || isCommandsActive;
  const [clientFirstName = '', ...clientLastNameParts] = (clientName || '').trim().split(/\s+/).filter(Boolean);
  const clientLastName = clientLastNameParts.join(' ');

  const clientRelationships = React.useMemo(() => {
    const relationships = Object.values(cardItem.relationships ?? {});
    if (!relationships.length) return [];

    return relationships.slice().sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    });
  }, [cardItem.relationships]);

  const primaryRelationship = clientRelationships[0];
  const notSetLabel = translate('clientLead.ClientLeadList.Card2.SubtitleValue.NotSet');
  const clientLocation = clientCareplan?.generalInformation?.address || notSetLabel;
  const clientCreatedAtLabel = clientCreatedAt ? moment(clientCreatedAt).format('MMM D, YYYY') : '-';
  const caregiverCount = Object.keys(cardItem.caregivers ?? {}).length;

  const clientDaysInStage = React.useMemo(() => {
    if (!clientStatusLog) {
      return Math.floor(moment().diff(moment(clientCreatedAt), 'days', true));
    } else {
      return Math.floor(moment().diff(moment(clientStatusLog.createdAt), 'days', true));
    }
  }, [clientStatusLog, clientCreatedAt]);

  const clientDaysInStageText = React.useMemo(() => {
    return translate(
        `clientLead.ClientLeadList.Card.SubtitleValue.${clientDaysInStage === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysInStage},
    );
  }, [clientDaysInStage]);

  const clientDaysSinceLastContact = React.useMemo(() => {
    if (!clientLastMessage) return null;
    return Math.floor(moment().diff(moment(clientLastMessage.createdAt), 'days', true));
  }, [clientLastMessage]);

  const clientDaysSinceLastContactText = React.useMemo(() => {
    if (clientDaysSinceLastContact === null) return null;
    return translate(
        `clientLead.ClientLeadList.Card.SubtitleValue.${clientDaysSinceLastContact === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysSinceLastContact},
    );
  }, [clientDaysSinceLastContact]);

  const handleSetStatus = async (nextStatus, message = null) => {
    if (!nextStatus || nextStatus === status) {
      return;
    }

    const previousItem = cardItem;
    const optimisticItem = {
      ...cardItem,
      status: nextStatus,
      clientLastLogStatusUpdate: {
        ...cardItem.clientLastLogStatusUpdate,
        createdAt: new Date().toISOString(),
      },
    };

    setCardItem(optimisticItem);
    onItemUpdate?.(clientRn, optimisticItem);

    try {
      await progressWrap(async () => {
        const body = {
          status: nextStatus,
        };

        if (message) {
          body.message = message;
        }

        const {clientDetail, updateLog} = await backend.client.updateStatus(clientRn, body);
        const nextItem = {
          ...optimisticItem,
          ...clientDetail,
          clientLastLogStatusUpdate: updateLog ?? clientDetail.clientLastLogStatusUpdate,
        };

        setCardItem(nextItem);
        onItemUpdate?.(clientRn, nextItem);
      });
    } catch (error) {
      setCardItem(previousItem);
      onItemUpdate?.(clientRn, previousItem);
      console.error(error);
    }
  };

  const handleSetCallStatus = async (nextCallStatus) => {
    if (!nextCallStatus || nextCallStatus === callStatus) {
      return;
    }

    const previousItem = cardItem;
    const optimisticItem = {
      ...cardItem,
      clientCallStatus: {
        ...(cardItem.clientCallStatus ?? {}),
        introCallStatus: nextCallStatus,
      },
    };

    setCardItem(optimisticItem);
    onItemUpdate?.(clientRn, optimisticItem);

    try {
      await progressWrap(async () => {
        const {callStatus: nextCallStatusData} = await backend.client.editCallStatus(clientRn, {
          introCallStatus: nextCallStatus,
        });
        const nextItem = {
          ...optimisticItem,
          clientCallStatus: nextCallStatusData,
        };

        setCardItem(nextItem);
        onItemUpdate?.(clientRn, nextItem);
      });
    } catch (error) {
      setCardItem(previousItem);
      onItemUpdate?.(clientRn, previousItem);
      console.error(error);
    }
  };

  const leadPlaybook = React.useMemo(() => ({
    execute: (playbookRn, resourceRns) => backend.playbook.execution.create(playbookRn, resourceRns),
  }), []);

  const leadResources = React.useMemo(() => [
    {type: 'CLIENT', rn: clientRn, firstName: clientFirstName, lastName: clientLastName},
  ], [clientRn, clientFirstName, clientLastName]);

  const footer = (
    <Stack
      direction='column'
      sx={{
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box
        onFocus={() => setIsCommandsFocused(true)}
        onBlur={() => setIsCommandsFocused(false)}
      >
        <Commands
          autoFocus={isCommandsActive}
          onInputValueChange={(value) => setIsCommandActive(Boolean(value))}
          playbook={leadPlaybook}
          resources={leadResources}
          statusProgressWrap={progressWrap}
        />
      </Box>
      {
        hasUnreadMessage &&
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{px: 1.5, pb: 1}}
        >
          <MailOutline
            sx={{
              fontSize: '16px',
              color: '#DA7955',
            }}
          />
          <Typography
            component='div'
            sx={{
              color: '#DA7955',
              fontSize: '12px',
              fontWeight: 400,
            }}
          >
            <Translate expression='clientLead.ClientLeadList.Card.Subtitles.NewMessage' />
          </Typography>
        </Stack>
      }
    </Stack>
  );

  const cardBody = (
    <CardBody
      callStatus={callStatus}
      caregiverCount={caregiverCount}
      clientCreatedAtLabel={clientCreatedAtLabel}
      clientDaysInStageText={clientDaysInStageText}
      clientDaysSinceLastContactText={clientDaysSinceLastContactText}
      clientLastMessage={clientLastMessage}
      clientLocation={clientLocation}
      clientName={clientName}
      hasUnreadMessage={hasUnreadMessage}
      leadQuality={leadQuality}
      notSetLabel={notSetLabel}
      onCallStatusOverlayChange={setIsCallStatusDropdownOpen}
      onStatusOverlayChange={setIsStatusDropdownOpen}
      primaryRelationship={primaryRelationship}
      selectedCallStatus={callStatus}
      selectedStage={status}
      setSelectedCallStatus={handleSetCallStatus}
      setSelectedStage={handleSetStatus}
      footer={footer}
    />
  );

  if (isCommandsActive) {
    return (
      <Box
        sx={{
          height: '100%',
          textAlign: 'left',
          width: '100%',
          minWidth: 0,
        }}
      >
        {cardBody}
      </Box>
    );
  }

  return (
    <CardActionArea
      onClick={(event) => {
        if (!isAnyOverlayOpen) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
      }}
      sx={{
        height: '100%',
        textAlign: 'left',
        width: '100%',
        minWidth: 0,
      }}
      to={`${clientSource.detailPath}/${clientRn}`}
    >
      {cardBody}
    </CardActionArea>
  );
};

Card.propTypes = {
  item: PropTypes.object.isRequired,
  onItemUpdate: PropTypes.func,
  hasUnreadMessages: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  clientSource: PropTypes.object,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(Card));
